import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api';
import '../../styles/CertificationManagement.css';

const CertificationManagement = () => {
  const navigate = useNavigate();
  const [certifications, setCertifications] = useState([]);
  const [allSubjects, setAllSubjects] = useState([]);
  const [filteredCertifications, setFilteredCertifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedRow, setExpandedRow] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [currentCert, setCurrentCert] = useState(null);
  
  const [formData, setFormData] = useState({
    certification_name: '',
    code: '',
    subjects: []
  });

  useEffect(() => {
    fetchCertifications();
    fetchAllSubjects();
  }, []);

  useEffect(() => {
    filterCertifications();
  }, [searchTerm, certifications]);

  // Handle Escape key
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        if (showModal) {
          setShowModal(false);
        } else {
          navigate('/admin/dashboard');
        }
      }
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [navigate, showModal]);

  const fetchCertifications = async () => {
    try {
      const response = await api.get('/master/certifications.php');
      console.log('Certifications Response:', response.data);
      if (response.data.success) {
        setCertifications(response.data.data);
        setFilteredCertifications(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching certifications:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchAllSubjects = async () => {
    try {
      const response = await api.get('/master/certifications.php?action=subjects');
      console.log('Subjects Response:', response.data);
      if (response.data.success) {
        setAllSubjects(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching subjects:', error);
      // Fallback: try to get from subjects API directly
      try {
        const fallbackResponse = await api.get('/master/subjects.php');
        if (fallbackResponse.data.success) {
          setAllSubjects(fallbackResponse.data.data);
        }
      } catch (fallbackError) {
        console.error('Fallback subjects fetch also failed:', fallbackError);
      }
    }
  };

  const filterCertifications = () => {
    if (!searchTerm) {
      setFilteredCertifications(certifications);
      return;
    }

    const filtered = certifications.filter(cert =>
      cert.certification_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      cert.code?.toLowerCase().includes(searchTerm.toLowerCase())
    );
    setFilteredCertifications(filtered);
  };

  const handleAdd = () => {
    setEditMode(false);
    setCurrentCert(null);
    setFormData({
      certification_name: '',
      code: '',
      subjects: []
    });
    setShowModal(true);
  };

  const handleEdit = async (cert) => {
    setEditMode(true);
    setCurrentCert(cert);
    
    try {
      const response = await api.get(`/master/certifications.php?id=${cert.id}`);
      if (response.data.success) {
        const certData = response.data.data;
        setFormData({
          id: certData.id,
          certification_name: certData.certification_name,
          code: certData.code || '',
          subjects: certData.subjects.map(s => ({
            subject_id: s.subject_id,
            fees: s.fees,
            is_applicable: true
          }))
        });
        setShowModal(true);
      }
    } catch (error) {
      alert('Failed to load certification details');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this certification?')) {
      return;
    }

    try {
      const response = await api.post('/master/certifications.php', {
        action: 'delete',
        id: id
      });

      if (response.data.success) {
        alert('Deleted successfully!');
        fetchCertifications();
      }
    } catch (error) {
      alert('Failed to delete certification');
    }
  };

  const handleToggleRow = (id) => {
    setExpandedRow(expandedRow === id ? null : id);
  };

  const handleSubjectToggle = (subjectId) => {
    const existingIndex = formData.subjects.findIndex(s => s.subject_id === subjectId);
    
    if (existingIndex >= 0) {
      setFormData(prev => ({
        ...prev,
        subjects: prev.subjects.filter(s => s.subject_id !== subjectId)
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        subjects: [...prev.subjects, { subject_id: subjectId, fees: 0, is_applicable: true }]
      }));
    }
  };

  const handleFeeChange = (subjectId, fees) => {
    setFormData(prev => ({
      ...prev,
      subjects: prev.subjects.map(s =>
        s.subject_id === subjectId ? { ...s, fees: parseFloat(fees) || 0 } : s
      )
    }));
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (formData.subjects.length === 0) {
      alert('Please select at least one subject');
      return;
    }

    try {
      const response = await api.post('/master/certifications.php', {
        action: editMode ? 'update' : 'add',
        ...formData
      });

      if (response.data.success) {
        alert(editMode ? 'Updated successfully!' : 'Added successfully!');
        setShowModal(false);
        fetchCertifications();
      }
    } catch (error) {
      alert(error.response?.data?.message || 'Failed to save certification');
    }
  };

  const isSubjectSelected = (subjectId) => {
    return formData.subjects.some(s => s.subject_id === subjectId);
  };

  const getSubjectFee = (subjectId) => {
    const subject = formData.subjects.find(s => s.subject_id === subjectId);
    return subject ? subject.fees : 0;
  };

  return (
    <div className="certification-management-page">
      <div className="certification-management-container">
        <div className="certification-header">
          <div className="header-left">
            <button className="back-button" onClick={() => navigate('/admin/dashboard')} title="Back (ESC)">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7" />
              </svg>
            </button>
            <div>
              <h2>Certification Management</h2>
              <p>Manage certifications and their subjects</p>
            </div>
          </div>
          <button className="btn-add" onClick={handleAdd}>
            ➕ Add Certification
          </button>
        </div>

        <div className="certification-search">
          <input
            type="text"
            placeholder="Search certifications..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>

        <div className="certification-table-container">
          {loading ? (
            <div className="loading">Loading...</div>
          ) : filteredCertifications.length === 0 ? (
            <div className="no-data">No certifications found</div>
          ) : (
            <table className="certification-table">
              <thead>
                <tr>
                  <th width="50"></th>
                  <th>SR.</th>
                  <th>CERTIFICATION</th>
                  <th>CODE</th>
                  <th>SUBJECTS COUNT</th>
                  <th>ACTIONS</th>
                </tr>
              </thead>
              <tbody>
                {filteredCertifications.map((cert, index) => (
                  <React.Fragment key={cert.id}>
                    <tr>
                      <td>
                        <button className="expand-btn" onClick={() => handleToggleRow(cert.id)}>
                          {expandedRow === cert.id ? '▼' : '▶'}
                        </button>
                      </td>
                      <td>{index + 1}</td>
                      <td>
                        <div className="cert-name-badge">
                          🎓 {cert.certification_name}
                        </div>
                      </td>
                      <td>{cert.code || '-'}</td>
                      <td>
                        <span className="subject-count-badge">
                          {cert.subjects_count} subjects
                        </span>
                      </td>
                      <td className="action-buttons">
                        <button className="btn-edit" onClick={() => handleEdit(cert)} title="Edit">
                          ✏️
                        </button>
                        <button className="btn-delete" onClick={() => handleDelete(cert.id)} title="Delete">
                          🗑️
                        </button>
                      </td>
                    </tr>
                    {expandedRow === cert.id && cert.subjects && cert.subjects.length > 0 && (
                      <tr className="expanded-row">
                        <td colSpan="6">
                          <div className="subjects-container">
                            <h4>Selected Subjects</h4>
                            <table className="subjects-table">
                              <thead>
                                <tr>
                                  <th>Subject</th>
                                  <th>Fees (Rs.)</th>
                                </tr>
                              </thead>
                              <tbody>
                                {cert.subjects.map((subject) => (
                                  <tr key={subject.id}>
                                    <td>{subject.subject_name}</td>
                                    <td>₹{parseFloat(subject.fees).toFixed(2)}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          )}
        </div>

        <div className="certification-summary">
          <p>Total: <strong>{filteredCertifications.length}</strong> of {certifications.length}</p>
        </div>
      </div>

      {/* Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal-content-large" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{editMode ? 'Edit Certification' : 'Add New Certification'}</h3>
              <button className="close-modal" onClick={() => setShowModal(false)}>✕</button>
            </div>
            <form onSubmit={handleSubmit} className="certification-form">
              <div className="form-row">
                <div className="form-group">
                  <label>Certification Name <span className="required">*</span></label>
                  <input
                    type="text"
                    name="certification_name"
                    value={formData.certification_name}
                    onChange={handleChange}
                    placeholder="Enter certification name"
                    required
                  />
                </div>

                <div className="form-group">
                  <label>Code</label>
                  <input
                    type="text"
                    name="code"
                    value={formData.code}
                    onChange={handleChange}
                    placeholder="Enter code"
                  />
                </div>
              </div>

              {/* REMOVED DESCRIPTION FIELD */}

              <div className="subjects-selection">
                <div className="subjects-header">
                  <h4>Select Applicable Subjects & Configure Fees</h4>
                  <p className="helper-text">(*) Indicates that the fields are mandatory for applicable subjects</p>
                  {allSubjects.length === 0 && (
                    <button
                      type="button"
                      className="btn-add-subject"
                      onClick={() => navigate('/admin/master/subjects')}
                    >
                      ➕ Add Subject
                    </button>
                  )}
                </div>

                {allSubjects.length > 0 ? (
                  <table className="subjects-selection-table">
                    <thead>
                      <tr>
                        <th>SUBJECT</th>
                        <th>APPLICABLE (YES/NO)</th>
                        <th>FEES (RS.) *</th>
                      </tr>
                    </thead>
                    <tbody>
                      {allSubjects.map((subject) => {
                        const isChecked = isSubjectSelected(subject.id);
                        return (
                          <tr key={subject.id} className={isChecked ? 'selected-row' : ''}>
                            <td>{subject.subject_name}</td>
                            <td>
                              <input
                                type="checkbox"
                                checked={isChecked}
                                onChange={() => handleSubjectToggle(subject.id)}
                                className="subject-checkbox"
                              />
                            </td>
                            <td>
                              <input
                                type="number"
                                value={getSubjectFee(subject.id)}
                                onChange={(e) => handleFeeChange(subject.id, e.target.value)}
                                placeholder="0.00"
                                step="0.01"
                                min="0"
                                disabled={!isChecked}
                                className="fee-input"
                              />
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                ) : (
                  <div className="no-subjects-message">
                    <p>No subjects available. Please add subjects first.</p>
                    <button
                      type="button"
                      className="btn-add-subject-large"
                      onClick={() => navigate('/admin/master/subjects')}
                    >
                      ➕ Go to Manage Subjects
                    </button>
                  </div>
                )}
              </div>

              <div className="form-actions">
                <button type="button" className="btn-cancel" onClick={() => setShowModal(false)}>
                  Cancel
                </button>
                <button type="submit" className="btn-save" disabled={allSubjects.length === 0}>
                  {editMode ? 'Update' : 'Save'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default CertificationManagement;